export type Header = {
  text: string
  value: string
}

export type Cost = {
  id: string
  section: string
  type: string | null
  rate: {
    value: number
    unit: string
  } | null,
  price: {
    value: number
    unit: string
  } | null,
  cost: number
  tip?: string
  noDuplicate?: boolean
}

export type Assumption = {
  id: string
  section: string
  type: string
  tip: string
  value: any
  unit: string
  items?: any[]
  readonly?: boolean
}

export interface Fields {
  costsHeaders: Header[]
  assumptionsHeaders: Header[]
  costs: {
    perennial: Cost[],
    shortTerm: Cost[]
  }
  assumptions: {
    perennial: Assumption[]
    shortTerm: Assumption[]
  }
}

const fields: Fields = {
  costsHeaders: [
    { text: 'Section', value: 'section' },
    { text: 'Type', value: 'type' },
    { text: 'Rate', value: 'rate' },
    { text: 'Price', value: 'price' },
    { text: 'Cost ($/ha)', value: 'cost' },
    { text: 'Action', value: 'noDuplicate' }
  ],
  assumptionsHeaders: [
    { text: 'Section', value: 'section' },
    { text: 'Type', value: 'type' },
    { text: 'Value', value: 'value' }
  ],
  costs: {
    perennial: [
      {
        id: 'fencing',
        section: 'Paddock infrastructure',
        type: 'Fencing',
        rate: {
          value: 0,
          unit: 'kilometres'
        },
        price: {
          value: 0,
          unit: 'km'
        },
        cost: 0,
        noDuplicate: true
      },
      {
        id: 'pipes',
        section: 'Paddock infrastructure',
        type: 'Pipes',
        rate: {
          value: 0,
          unit: 'metres'
        },
        price: {
          value: 0,
          unit: 'm'
        },
        cost: 0,
        noDuplicate: true
      },
      {
        id: 'levelling',
        section: 'Paddock infrastructure',
        type: 'Levelling',
        rate: {
          value: 0,
          unit: 'hectares'
        },
        price: {
          value: 0,
          unit: 'ha'
        },
        cost: 0,
        noDuplicate: true
      },
      {
        id: 'troughs',
        section: 'Paddock infrastructure',
        type: 'Troughs',
        rate: {
          value: 0,
          unit: 'troughs'
        },
        price: {
          value: 0,
          unit: 'trough'
        },
        cost: 0,
        noDuplicate: true
      },
      {
        id: 'otherInfrastructure',
        section: 'Paddock infrastructure',
        type: 'Other Infrastructure',
        rate: null,
        price: {
          value: 0,
          unit: 'ha'
        },
        cost: 0,
        noDuplicate: true
      },
      {
        id: 'cultivation',
        section: 'Cultivation',
        type: null,
        rate: {
          value: 0,
          unit: 'passes'
        },
        price: {
          value: 0,
          unit: 'ha'
        },
        cost: 0
      },
      {
        id: 'limeGypsum',
        section: 'Lime / Gypsum',
        type: null,
        rate: {
          value: 0,
          unit: 'T/ha'
        },
        price: {
          value: 0,
          unit: 'tonne'
        },
        cost: 0
      },
      {
        id: 'seed',
        section: 'Seed',
        type: null,
        rate: {
          value: 0,
          unit: 'kg/ha'
        },
        price: {
          value: 0,
          unit: 'kg'
        },
        cost: 0
      },
      {
        id: 'fertiliser',
        section: 'Fertiliser',
        type: null,
        rate: {
          value: 0,
          unit: 'kg/ha'
        },
        price: {
          value: 0,
          unit: 'tonne'
        },
        cost: 0
      },
      {
        id: 'springSpraying',
        section: 'Spring spraying',
        type: null,
        rate: {
          value: 0,
          unit: 'L/ha'
        },
        price: {
          value: 0,
          unit: 'L'
        },
        cost: 0
      },
      {
        id: 'presowSpraying',
        section: 'Pre-sow spraying',
        type: null,
        rate: {
          value: 0,
          unit: 'L/ha'
        },
        price: {
          value: 0,
          unit: 'L'
        },
        cost: 0
      },
      {
        id: 'postsowSpraying',
        section: 'Post-sow spraying',
        type: null,
        rate: {
          value: 0,
          unit: 'L/ha'
        },
        price: {
          value: 0,
          unit: 'L'
        },
        cost: 0
      },
      {
        id: 'fertiliserApplication',
        section: 'Fertiliser application',
        type: null,
        tip: 'What is the cost of fertiliser that will be applied to this paddock after resowing above what would normally be applied? If additional fertiliser is only applied every second or third year, show the average cost of additional fertiliser per year.',
        rate: {
          value: 0,
          unit: 'kg/ha'
        },
        price: {
          value: 0,
          unit: 'tonne'
        },
        cost: 0
      },
      {
        id: 'herbicidesPesticides',
        section: 'Herbicides / pesticides',
        type: null,
        tip: 'What is the cost of sprays or other treatments that will be applied to this paddock after resowing above what would normally be applied? If additional treatments are only applied every second or third year, show the average cost of additional fertiliser per year.',
        rate: {
          value: 0,
          unit: 'L/ha'
        },
        price: {
          value: 0,
          unit: 'L'
        },
        cost: 0
      },
      {
        id: 'additionalLivestockLabour',
        section: 'Additional livestock labour',
        type: null,
        tip: 'It is assumed that the resown pasture will allow a greater carrying capacity than previously possible. Depending on the type of stock, size of mobs, increase in absolute numbers, this may increase the labour required to manage the stock. In this area show the estimated cost of additional labour per year required to manage the new livestock system.',
        rate: null,
        price: {
          value: 0,
          unit: 'DSE'
        },
        cost: 0,
        noDuplicate: true
      }
    ],
    shortTerm: []
  },
  assumptions: {
    perennial: [
      {
        id: 'pastureLife',
        section: 'Production',
        type: 'Expected pasture / infrastructure life',
        tip: 'The life of pasture has a large impact on the overall profitability of resowing pastures. This value is the number of years before the pasture needs to be resown.',
        value: null,
        unit: '4-20 years',
        items: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]
      },
      {
        id: 'chanceOfFailure',
        section: 'Production',
        type: 'Chance of pasture failure',
        tip: 'How often you expect the establishment to be a complete failure due to weather, insects, weeds, etc. requiring complete resowing the following year. 10% equals 1 in 10 failues. Failure means the seed and sprays must be replaced and grazing is lost when the pasture is resown.',
        value: null,
        unit: '%',
      },
      {
        id: 'stockingRateBefore',
        section: 'Production',
        type: 'Stocking rate before improvement',
        tip: 'What is the current stocking rate on the pasture before resowing?',
        value: null,
        unit: 'DSE/ha',
      },
      {
        id: 'stockingRateAfter',
        section: 'Production',
        type: 'Peak stocking rate after improvement',
        tip: 'What stocking rate do you expect to be able to carry after resowing? Peak stocking rate after resowing should be estimated from experience, farm records, results from local trials etc.',
        value: null,
        unit: 'DSE/ha',
      },
      {
        id: 'stockingRateTimeToPeak',
        section: 'Production',
        type: 'Time to reach peak stocking rate',
        tip: 'How long will it take for the new pasture to reach the peak carrying capacity?',
        value: null,
        unit: '1-5 years',
        items: [1, 2, 3, 4, 5]
      },
      {
        id: 'stockingRateDeclineYear',
        section: 'Production',
        type: 'Year when stocking rate begins to decline',
        tip: 'When will the pasture start to decline in production?',
        value: null,
        unit: 'year between 6-20',
        items: [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]
      },
      {
        id: 'stockingRateEndOfLife',
        section: 'Production',
        type: 'Stocking rate at end of pasture / infrastructure life',
        tip: 'What is the expected stocking rate at the end of pasture / infrastructure life. May be higher than orginal stocking rate.',
        value: null,
        unit: 'DSE/ha',
      },
      {
        id: 'grazedWeeks',
        section: 'Production',
        type: 'Number of weeks pasture grazed in year of sowing',
        tip: 'During resowing, stock that would normally graze the paddock needs agistment or to graze elsewhere. Some pastures take longer to establish than others.',
        value: null,
        unit: 'weeks',
      },
      {
        id: 'agistmentCost',
        section: 'Economic & financial',
        type: 'Agistment cost',
        tip: 'What is the cost, if any, of additional feeding or agistment while the paddock is unable to be grazed?',
        value: null,
        unit: '$/DSE/week',
      },
      {
        id: 'grossMarginBefore',
        section: 'Economic & financial',
        type: 'Gross margin before improvement',
        tip: 'What is the current gross margin of the stock that graze the pasture?',
        value: null,
        unit: '$/DSE',
      },
      {
        id: 'grossMarginAfter',
        section: 'Economic & financial',
        type: 'Gross margin at peak stocking rate',
        tip: 'What is the estimated gross margin of the stock after resowing? It would be expected that stock grazing resown pastures will perform better than on degraded pastures.',
        value: null,
        unit: '$/DSE',
      },
      {
        id: 'livestockCapital',
        section: 'Economic & financial',
        type: 'Capital cost of livestock',
        tip: 'There will be a need for extra stock to graze the resown pasture. What is the estimated cost to purchase stock?',
        value: null,
        unit: '$/DSE',
      },
      {
        id: 'opportunityCost',
        section: 'Economic & financial',
        type: 'Opportunity cost of invested capital (discount rate)',
        tip: 'Also called the Discount rate: the minimum % return on initial capital spent to improve pasture that the farmer wishes to earn over the life of the investment. The default value is 12%.',
        value: 12,
        unit: '%',
      },
      {
        id: 'annualInflationRate',
        section: 'Economic & financial',
        type: 'Expected annual inflation rate',
        tip: 'What do you think will be the inflation rate over the period of this comparison? The default value is 2.5%.',
        value: 2.5,
        unit: '%',
      },
      {
        id: 'marginalTaxRate',
        section: 'Economic & financial',
        type: 'Marginal tax rate',
        tip: 'Tax rate paid on additional income. The default value is 20%.',
        value: 20,
        unit: '%',
      },
      {
        id: 'interestBorrowed',
        section: 'Economic & financial',
        type: 'Interest of borrowed funds',
        tip: 'Interest rate payed on any negative annual cash flows. Average overdraft interest rate would be a reasonable figure to use. The default value is 8%.',
        value: 8,
        unit: '%',
      },
      {
        id: 'interestInvestment',
        section: 'Economic & financial',
        type: 'Interest of investment funds',
        tip: 'Interest earned on any positive annual cash flows. Interest rate on a farm account would be a reasonable figure to use. The default value is 5.5%.',
        value: 5.5,
        unit: '%',
      },
      {
        id: 'residualSeedSpray',
        section: 'Residual',
        type: 'Seed and sprays',
        tip: 'At the assumed end of pasture life, what, if any, value will remain from the initial seed sowing and spraying program?',
        value: null,
        unit: '$/ha',
      },
      {
        id: 'residualLimeGypsum',
        section: 'Residual',
        type: 'Lime / Gypsum',
        tip: 'At the assumed end of pasture life, what, if any, value will remain from the lime and gypsum applied during the established program?',
        value: null,
        unit: '$/ha',
      },
      {
        id: 'residualFertiliser',
        section: 'Residual',
        type: 'Fertiliser',
        tip: 'Resown pasture is normally given priority for fertliser to improve persistence and production of introduced species. If soil P is expected to be higher at the end of the program than prior to resowing, this P bank in the soil needs to be valued.',
        value: null,
        unit: '$/ha',
      },
      {
        id: 'residualSoilN',
        section: 'Residual',
        type: 'Soil N (high legume content)',
        tip: 'If the resowing program increases the legume content of the pasture, it will increase soil N by 25kg/tonne extra legume grown. This will especially benefit any subsequent grain crops sown in the paddock.',
        value: null,
        unit: '$/ha',
      },
      {
        id: 'residualPastureInfrastructure',
        section: 'Residual',
        type: 'Pasture infrastructure',
        tip: 'The value of any paddock development improvements remaining at the end of pasture life e.g. fencing.',
        value: null,
        unit: '$/ha',
      },
      {
        id: 'environmentalBenefit',
        section: 'Environment',
        type: 'Annual environmental benefit',
        tip: 'Pastures may be resown to reduce environmental problems as well as improve animal production. Environmental benefits can include improved ground cover and increased perenniality.',
        value: null,
        unit: '$/ha',
      }
    ],
    shortTerm: [
      {
        id: 'pastureLife',
        section: 'Production',
        type: 'Expected pasture / infrastructure life',
        tip: 'The life of pasture has a large impact on the overall profitability of resowing pastures. This value is the number of years before the pasture needs to be resown.',
        value: null,
        unit: '1-20 years',
        items: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]
      },
      {
        id: 'frequencyOfResowingPasture',
        section: 'Production',
        type: 'Frequency of resowing pasture',
        tip: 'The life of the pasture has a large impact on overall profitability of resowing pastures. The value is the number of years before the pasture has to be resown.',
        value: 'Annually',
        unit: 'Frequency',
        items: ['Annually', 'Every 2nd year', 'Every 3rd year'],
      },
      {
        id: 'chanceOfFailure',
        section: 'Production',
        type: 'Chance of pasture failure',
        tip: 'How often you expect the establishment to be a complete failure due to weather, insects, weeds, etc. requiring complete resowing the following year. 10% equals 1 in 10 failues. Failure means the seed and sprays must be replaced and grazing is lost when the pasture is resown.',
        value: null,
        unit: '%',
      },
      {
        id: 'stockingRateBefore',
        section: 'Production',
        type: 'Stocking rate before improvement',
        tip: 'What is the current stocking rate on the pasture before resowing?',
        value: null,
        unit: 'DSE/ha',
        // readonly: true
      },
      {
        id: 'stockingRateAfter',
        section: 'Production',
        type: 'Peak stocking rate after improvement',
        tip: 'What stocking rate do you expect to be able to carry after resowing? Peak stocking rate after resowing should be estimated from experience, farm records, results from local trials etc.',
        value: null,
        unit: 'DSE/ha',
      },
      {
        id: 'grazedWeeks',
        section: 'Production',
        type: 'Number of weeks pasture grazed in year of sowing',
        tip: 'During resowing, stock that would normally graze the paddock needs agistment or to graze elsewhere. Some pastures take longer to establish than others.',
        value: null,
        unit: 'weeks',
      },
      {
        id: 'agistmentCost',
        section: 'Economic & financial',
        type: 'Agistment cost',
        tip: 'What is the cost, if any, of additional feeding or agistment while the paddock is unable to be grazed?',
        value: null,
        unit: '$/DSE/week',
      },
      {
        id: 'grossMarginBefore',
        section: 'Economic & financial',
        type: 'Gross margin before improvement',
        tip: 'What is the current gross margin of the stock that graze the pasture?',
        value: null,
        unit: '$/DSE',
        // readonly: true
      },
      {
        id: 'grossMarginAfter',
        section: 'Economic & financial',
        type: 'Gross margin at peak stocking rate',
        tip: 'What is the estimated gross margin of the stock after resowing? It would be expected that stock grazing resown pastures will perform better than on degraded pastures.',
        value: null,
        unit: '$/DSE',
      },
      {
        id: 'livestockCapital',
        section: 'Economic & financial',
        type: 'Capital cost of livestock',
        tip: 'There will be a need for extra stock to graze the resown pasture. What is the estimated cost to purchase stock?',
        value: null,
        unit: '$/DSE',
      },
      {
        id: 'annualInflationRate',
        section: 'Economic & financial',
        type: 'Expected annual inflation rate',
        tip: 'What do you think will be the inflation rate over the period of this comparison? The default value is 2.5%.',
        value: 2.5,
        unit: '%',
      },
      {
        id: 'residualSeedSpray',
        section: 'Residual',
        type: 'Seed and sprays',
        tip: 'At the assumed end of pasture life, what, if any, value will remain from the initial seed sowing and spraying program?',
        value: null,
        unit: '$/ha',
      },
      {
        id: 'residualLimeGypsum',
        section: 'Residual',
        type: 'Lime / Gypsum',
        tip: 'At the assumed end of pasture life, what, if any, value will remain from the lime and gypsum applied during the established program?',
        value: null,
        unit: '$/ha',
      },
      {
        id: 'residualFertiliser',
        section: 'Residual',
        type: 'Fertiliser',
        tip: 'Resown pasture is normally given priority for fertliser to improve persistence and production of introduced species. If soil P is expected to be higher at the end of the program than prior to resowing, this P bank in the soil needs to be valued.',
        value: null,
        unit: '$/ha',
      },
      {
        id: 'residualSoilN',
        section: 'Residual',
        type: 'Soil N (high legume content)',
        tip: 'If the resowing program increases the legume content of the pasture, it will increase soil N by 25kg/tonne extra legume grown. This will especially benefit any subsequent grain crops sown in the paddock.',
        value: null,
        unit: '$/ha',
      },
      {
        id: 'residualPastureInfrastructure',
        section: 'Residual',
        type: 'Pasture infrastructure',
        tip: 'The value of any paddock development improvements remaining at the end of pasture life e.g. fencing.',
        value: null,
        unit: '$/ha',
      },
      {
        id: 'environmentalBenefit',
        section: 'Environment',
        type: 'Annual environmental benefit',
        tip: 'Pastures may be resown to reduce environmental problems as well as improve animal production. Environmental benefits can include improved ground cover and increased perenniality.',
        value: null,
        unit: '$/ha',
      }
    ]
  }
}

// Shift short term additional livestock labour to dev costs instead of main costs
fields.costs.shortTerm = [...fields.costs.perennial]
let fromIndex = fields.costs.shortTerm.map((c:Cost) => c.id).indexOf('additionalLivestockLabour')
let toIndex = fields.costs.shortTerm.map((c:Cost) => c.id).indexOf('fertiliserApplication')
let element = fields.costs.shortTerm[fromIndex];
fields.costs.shortTerm.splice(fromIndex, 1);
fields.costs.shortTerm.splice(toIndex, 0, element);

export default fields
